import React, { useEffect, useMemo, type ComponentType, useCallback } from 'react';
import '@atlaskit/css-reset';
import type { EntryPointProps } from 'react-relay';
import { useIntl } from '@atlassian/jira-intl';
import AppBase from '@atlassian/jira-app-base/src/index.tsx';
import AppStyle from '@atlassian/jira-common-components-app-style/src/index.tsx';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { getPreviousSubProduct } from '@atlassian/jira-platform-router-utils/src/common/utils/index.tsx';
import type { SafeHistory } from '@atlassian/jira-providers-history/src/safe-history/types.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id/src/index.tsx';
import { useRovoEntitlementResource } from '@atlassian/jira-router-resources-rovo-entitlement/src/services/index.tsx';

import { useIsFirstProjectITSM } from '@atlassian/jira-servicedesk-getting-started-page-common/src/common/controllers/first-project-itsm/useIsFirstProjectITSM.tsx';
import { useGettingStartedPanelState } from '@atlassian/jira-servicedesk-getting-started-panel-state/src/main.tsx';
import {
	JsdGettingStartedPanelOpenButton,
	JsdGettingStartedPanelShouldRender,
} from '@atlassian/jira-servicedesk-getting-started-panel/src/async.tsx';
import { useInitializeActiveTab } from '@atlassian/jira-servicedesk-getting-started-panel/src/services/hooks/useInitializeActiveTab.tsx';
import { MarkProductStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/mark-product-start/index.tsx';
import type { ReactRouterCompatibleHistory } from '@atlassian/jira-spa-router-adapters/src/common/types.tsx';
import { HistoryAdapter } from '@atlassian/jira-spa-router-adapters/src/controllers/history/index.tsx';
import { SpaStatePageReady } from '@atlassian/jira-spa-state-controller/src/components/main.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useEnvironment } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import EmbeddableDirectory from '@atlassian/ptc-embeddable-directory';
import { useService } from '@atlassian/jira-expansion-services/src/index.tsx';
import {
	fetch3PInviteRecommendations,
	mapToFeaturedUser,
	type Recommendations3PInput,
	type UserInfo,
	type MapToFeaturedUserInput,
} from '@atlassian/expansion-services/services';
import type { FeaturedUser } from '@atlassian/invite-people/types';
import { createShowFlagHandler } from './utils/create-show-flag-handler/index.tsx';
import { peopleProfilesPageLoad } from './utils/performance-analytics/index.tsx';
import {
	useInvitePeopleModal,
	InvitePeopleModalContextProvider,
} from './controllers/use-invite-people-modal/index.tsx';
import messages from './messages.tsx';

type History = ReactRouterCompatibleHistory | SafeHistory;
type Cohort = 'control' | 'variation';
type Props = {
	history: History;
};

const PeopleProfilesShell = ({ history }: Props) => {
	const { showFlag, dismissFlag } = useFlagsService();
	const [{ data: gspData }, { fetchState }] = useGettingStartedPanelState();
	useEffect(() => {
		if (gspData === undefined) {
			fetchState();
		}
	}, [gspData, fetchState]);
	const gspProjectId = gspData?.properties?.user?.projectId;
	const { isFirstProject, isFirstProjectItsm } = useIsFirstProjectITSM(gspProjectId);
	/**
	 * Initializes the active tab to 'alerts' within the Getting Started Panel
	 * specifically for NPLE for ITSM experiment.
	 */
	useInitializeActiveTab('alerts', gspProjectId);
	const { formatMessage } = useIntl();

	let fetchInviteRecommendationsService = useCallback(
		(_: Recommendations3PInput) => Promise.resolve<UserInfo[]>([]),
		[],
	);
	let mapToFeaturedUserService: (input: MapToFeaturedUserInput) => FeaturedUser[] = useCallback(
		(_: MapToFeaturedUserInput) => [],
		[],
	);

	if (fg('tepi-3p-core-invites-slack-rec-ux')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		fetchInviteRecommendationsService = useService(fetch3PInviteRecommendations);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		mapToFeaturedUserService = useService(mapToFeaturedUser);
	}

	const addFlag = useMemo(
		() => createShowFlagHandler(showFlag, dismissFlag),
		[showFlag, dismissFlag],
	);

	const { data: orgId } = useOrgId();
	const atlassianAccountId = useAccountId();
	const cloudId = useCloudId();
	const environment = useEnvironment();
	const locale = useLocale();
	const previousSubProduct = getPreviousSubProduct();

	const fetchInviteRecommendations = useCallback(async () => {
		if (fg('tepi-3p-core-invites-slack-rec-ux')) {
			const inviteRecommendationsFromSlack = await fetchInviteRecommendationsService({
				limit: 10,
			}).then((recommendations) => {
				return mapToFeaturedUserService({
					userInfo: recommendations,
					tooltipRenderer: (user: UserInfo) =>
						formatMessage(messages.inviteRecommendationsFromSlack, { email: user.email }),
				});
			});

			return inviteRecommendationsFromSlack;
		}
		return [];
	}, [fetchInviteRecommendationsService, mapToFeaturedUserService, formatMessage]);
	// legacy invite people modal requirements
	const invitePeopleExperimentalFeatures: Record<string, Cohort> = {
		inviteThirdPartyTeams: 'variation',
	};
	const { openModal } = useInvitePeopleModal({
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		continueUrl: window.location.href,
		enableThirdParty: true,
		experimentalFeatures: invitePeopleExperimentalFeatures,
		displayInfiSpotlight: true,
		resourceAri: `ari:cloud:jira::site/${cloudId}`,
		showFlag: addFlag,
		source: 'peopleDirectory',
		subProduct:
			previousSubProduct === 'unknown' || previousSubProduct === null
				? undefined
				: previousSubProduct,
		experimental_featuredUsers: fetchInviteRecommendations,
	});

	const invitePeopleHandlers = useMemo(
		() => ({
			handleOpenDialog: openModal,
		}),
		[openModal],
	);

	const { shouldUseJSM } = useOgTenantInfo();

	const { data: rovoEntitlement } = useRovoEntitlementResource();
	const isRovoEnabled = rovoEntitlement?.isRovoLLMEnabled;

	if (!atlassianAccountId) {
		return <LoginRedirect />;
	}

	const { push, replace } = history;

	const productFeatureFlags = {
		'event-on-call-consolidation_s97c8': true,
		'event-on-call-consolidation-state': shouldUseJSM,
	};

	const isNpleForItsmEnabled =
		isFirstProject &&
		isFirstProjectItsm &&
		expValEquals('jsm_getting_started_page_for_nple_exp', 'cohort', 'variation');

	return (
		<UFOSegment name="people-profiles">
			<MarkProductStart />
			<AppBase id="people-profiles">
				<AppStyle>
					<EmbeddableDirectory
						appName="jira-directory"
						contextSpaPaths={['/browse', '/jira', '/projects']}
						contextEntryPoint="/jira/people"
						env={environment === 'prod' ? 'prod' : 'stg'}
						locale={locale}
						// @ts-expect-error - TS2322 - Type '(flagFromPTC: FlagFromPTC) => void' is not assignable to type '(props: FlagProps) => void'.
						addFlag={addFlag}
						push={push}
						replace={replace}
						docTitle={{
							end: 'Jira',
						}}
						history={history}
						cloudId={cloudId}
						orgId={orgId}
						// @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'JiraSubProduct | undefined'.
						subProduct={previousSubProduct}
						invitePeopleHandlers={invitePeopleHandlers}
						productFeatureFlags={productFeatureFlags}
						isAIEnabled={!!isRovoEnabled}
					/>
					<SubmitApdex
						appName="people-profiles"
						isExpectedToHaveSsr={false}
						// @ts-expect-error - TS2769 - No overload matches this call.
						metric={peopleProfilesPageLoad}
					/>
					{fg('set-embedded-directory-spa-ready') && <SpaStatePageReady />}
				</AppStyle>
			</AppBase>
			{isNpleForItsmEnabled && (
				<>
					<JsdGettingStartedPanelShouldRender />
					<JsdGettingStartedPanelOpenButton />
				</>
			)}
		</UFOSegment>
	);
};

const PeopleProfiles: ComponentType<EntryPointProps<{}, {}, {}, {}>> = () => {
	return (
		// @ts-expect-error - TS2739 - Type '{ children: (history: any) => Element; }' is missing the following properties from type 'RouteContext': location, query, route, match, action
		<HistoryAdapter>
			{(history) => (
				<InvitePeopleModalContextProvider>
					<PeopleProfilesShell history={history} />
				</InvitePeopleModalContextProvider>
			)}
		</HistoryAdapter>
	);
};

export default PeopleProfiles;
